import { useEffect, useState } from "react";
import { api, inr, fmtDate, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { usePager, Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, BookOpen, Pencil, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import { VendorPayables } from "@/components/VendorPayables";

export default function Payments({ section = "receivables" }) {
  const [list, setList] = useState([]);
  const payPager = usePager(list);
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerName, setLedgerName] = useState("");
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ ptype: "invoice", invoice_id: "", sales_order_id: "", customer_id: "", amount: 0, mode: "Bank Transfer", reference: "" });

  const load = () => Promise.all([
    api.get("/payments").then((r) => setList(r.data)),
    api.get("/invoices").then((r) => setInvoices(r.data)),
    api.get("/sales-orders").then((r) => setOrders(r.data)),
    api.get("/customers").then((r) => setCustomers(r.data)),
    api.get("/payments/summary").then((r) => setSummary(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm({ ptype: "invoice", invoice_id: "", sales_order_id: "", customer_id: "", amount: 0, mode: "Bank Transfer", reference: "" });
  };

  const save = async () => {
    if (!(+form.amount > 0)) { toast.error("Enter a valid amount"); return; }
    try {
      if (form.ptype === "advance" && !editingId) {
        if (!form.sales_order_id) { toast.error("Select a sales order"); return; }
        await api.post("/payments/advance", { sales_order_id: form.sales_order_id, amount: +form.amount, mode: form.mode, reference: form.reference });
        toast.success("Advance recorded — it adjusts automatically against the order's invoices");
      } else if (form.ptype === "advance_customer" && !editingId) {
        if (!form.customer_id) { toast.error("Select a customer"); return; }
        await api.post("/payments/advance", { customer_id: form.customer_id, amount: +form.amount, mode: form.mode, reference: form.reference });
        toast.success("On-account advance recorded — it auto-adjusts against this customer's next invoice");
      } else if (form.ptype === "refund" && !editingId) {
        if (!form.invoice_id && !form.customer_id) { toast.error("Select an invoice or a customer"); return; }
        const rr = await api.post("/payments/refund", { invoice_id: form.invoice_id, customer_id: form.customer_id, amount: +form.amount, mode: form.mode, reference: form.reference });
        if (rr.data?.email_sent) toast.success(`Refund recorded — refund receipt emailed to ${rr.data.email_to}`);
        else toast.warning("Refund recorded, but the confirmation email could not be sent (no email on file or SMTP issue)");
      } else if (editingId && form.ptype === "advance_edit") {
        await api.put(`/payments/advance/${editingId}`, { amount: +form.amount, mode: form.mode, reference: form.reference });
        toast.success("Advance payment updated");
      } else if (editingId) {
        if (!form.invoice_id) { toast.error("Select an invoice"); return; }
        await api.put(`/payments/${editingId}`, { invoice_id: form.invoice_id, amount: +form.amount, mode: form.mode, reference: form.reference });
        toast.success("Payment updated");
      } else {
        if (!form.invoice_id) { toast.error("Select an invoice"); return; }
        await api.post("/payments", { invoice_id: form.invoice_id, amount: +form.amount, mode: form.mode, reference: form.reference });
        toast.success("Payment recorded");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendReceipt = async (p) => {
    if (!window.confirm(`Email this receipt of ${inr(p.amount)} to ${p.customer_name || "the customer"}? It will be sent from accounts@hitechsilico.com.`)) return;
    try {
      const r = await api.post(`/payments/${p.id}/send-receipt`);
      toast.success(`Receipt emailed to ${r.data.to}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    if (p.kind === "advance") {
      setForm({ ptype: "advance_edit", invoice_id: "", sales_order_id: "", customer_id: "", amount: p.amount, mode: p.mode || "Bank Transfer", reference: p.reference || "" });
    } else {
      setForm({ ptype: "invoice", invoice_id: p.invoice_id, sales_order_id: "", amount: p.amount, mode: p.mode || "Bank Transfer", reference: p.reference || "" });
    }
    setOpen(true);
  };

  const del = async (p) => {
    if (!window.confirm(`Delete this payment of ${inr(p.amount)}? The invoice balance will be restored.`)) return;
    try {
      await api.delete(`/payments/${p.id}`);
      toast.success("Payment deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openLedger = async (c) => {
    try {
      const r = await api.get(`/payments/ledger/${c.customer_id}`);
      setLedger(r.data); setLedgerName(c.customer);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="payments-page">
      {section === "payables" ? <VendorPayables /> : (<>
      <PageHeader title="Payments" subtitle="Received payments & customer ledger"
        right={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="pay-new"><Plus size={14} className="mr-1" /> Record Payment</Button>
            </DialogTrigger>
            <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
              <DialogHeader><DialogTitle>{editingId ? "Edit Payment" : "Record Payment"}</DialogTitle></DialogHeader>
              {!editingId && (
                <div className="flex gap-2" data-testid="pay-type-toggle">
                  {[["invoice", "Against Invoice"], ["advance", "Advance (Sales Order)"], ["advance_customer", "Advance (Customer)"], ["refund", "Refund (Paid Back)"]].map(([v, l]) => (
                    <button key={v} onClick={() => setForm({ ...form, ptype: v, invoice_id: "", sales_order_id: "", customer_id: "", amount: 0 })}
                            className={`text-xs px-4 py-2 rounded-full border font-medium transition-colors duration-150 ${form.ptype === v ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
                            data-testid={`pay-type-${v}`}>{l}</button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 content-start">
                {form.ptype === "advance" && !editingId ? (
                <div className="md:col-span-3">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Sales Order *</Label>
                  <Select value={form.sales_order_id} onValueChange={(v) => {
                    const so = orders.find((o) => o.id === v);
                    setForm({ ...form, sales_order_id: v, amount: so ? +(+so.total).toFixed(2) : form.amount });
                  }}>
                    <SelectTrigger className="mt-1.5 w-full h-11" data-testid="pay-so"><SelectValue placeholder="Select sales order" /></SelectTrigger>
                    <SelectContent>
                      {orders.filter((o) => o.status !== "Cancelled" && o.status !== "Closed").map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          <span className="font-mono text-xs mr-1">{o.number}</span>
                          <span className="text-slate-600">· {o.customer_snapshot?.company_name} · {inr(o.total)}{o.invoiced ? " · Invoiced" : ""}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-indigo-600 mt-1.5">Advance is held against the order and auto-adjusts the moment you create its invoice.</p>
                </div>
                ) : form.ptype === "advance_customer" && !editingId ? (
                <div className="md:col-span-3">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Customer *</Label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger className="mt-1.5 w-full h-11" data-testid="pay-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-indigo-600 mt-1.5">No order yet — the amount is held on account as advance credit and auto-adjusts against this customer's next invoice.</p>
                </div>
                ) : form.ptype === "advance_edit" ? (
                <div className="md:col-span-3">
                  <p className="text-[11px] text-indigo-600" data-testid="pay-advance-edit-note">Editing an advance payment — amount, mode and reference can be changed. Its order/customer link stays intact.</p>
                </div>
                ) : form.ptype === "refund" && !editingId ? (
                <div className="md:col-span-3 space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Refund against Invoice</Label>
                    <Select value={form.invoice_id || "none"} onValueChange={(v) => {
                      const inv = invoices.find((i) => i.id === v);
                      setForm({ ...form, invoice_id: v === "none" ? "" : v, customer_id: "", amount: inv ? +(+inv.paid).toFixed(2) : form.amount });
                    }}>
                      <SelectTrigger className="mt-1.5 w-full h-11" data-testid="pay-refund-invoice"><SelectValue placeholder="Select invoice (optional)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No invoice — refund on account</SelectItem>
                        {invoices.filter((i) => +i.paid > 0).map((i) => (
                          <SelectItem key={i.id} value={i.id}>
                            <span className="font-mono text-xs mr-1">{i.number}</span>
                            <span className="text-slate-600">· {i.customer_snapshot?.company_name} · Received {inr(i.paid)}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {!form.invoice_id && (
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-slate-500">Customer *</Label>
                      <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                        <SelectTrigger className="mt-1.5 w-full h-11" data-testid="pay-refund-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                        <SelectContent>
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <p className="text-[11px] text-rose-600">Money paid back to the customer (e.g. after rejected material). It reduces the invoice's received amount — once fully refunded, the invoice can be marked Returned.</p>
                </div>
                ) : (
                <div className="md:col-span-3">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Invoice *</Label>
                  <Select value={form.invoice_id} onValueChange={(v) => {
                    const inv = invoices.find((i) => i.id === v);
                    setForm({ ...form, invoice_id: v, amount: inv ? Math.max(0, +(inv.total - inv.paid).toFixed(2)) : form.amount });
                  }}>
                    <SelectTrigger className="mt-1.5 w-full h-11" data-testid="pay-invoice"><SelectValue placeholder="Select invoice" /></SelectTrigger>
                    <SelectContent>
                      {invoices.filter((i) => (i.status !== "Paid" && i.status !== "Returned") || i.id === form.invoice_id).map((i) => (
                        <SelectItem key={i.id} value={i.id}>
                          <span className="font-mono text-xs mr-1">{i.number}</span>
                          <span className="text-slate-600">· {i.customer_snapshot?.company_name} · Due {inr(i.total - i.paid)}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Amount *</Label>
                  <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 h-11" data-testid="pay-amount" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Mode</Label>
                  <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                    <SelectTrigger className="mt-1.5 w-full h-11" data-testid="pay-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="Cheque">Cheque</SelectItem>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Reference (UTR / Cheque No.)</Label>
                  <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5 h-11" data-testid="pay-reference" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="pay-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <Tabs defaultValue="payments">
        <TabsList>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Customer Ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="payments">
          <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2.5">Date</th>
                  <th className="text-left px-4 py-2.5">Invoice</th>
                  <th className="text-left px-4 py-2.5 hidden md:table-cell">Customer</th>
                  <th className="text-left px-4 py-2.5">Mode</th>
                  <th className="text-left px-4 py-2.5 hidden md:table-cell">Reference</th>
                  <th className="text-right px-4 py-2.5">Amount</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {payPager.pageItems.map((p) => {
                  const inv = invoices.find((i) => i.id === p.invoice_id);
                  return (
                    <tr key={p.id} className="border-t border-slate-100">
                      <td className="px-4 py-3">{fmtDate(p.date)}</td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {p.kind === "refund" ? (
                          <span className="inline-flex items-center text-[10px] uppercase tracking-wider bg-rose-50 border border-rose-200 text-rose-700 rounded px-1.5 py-0.5 font-sans font-medium" data-testid={`pay-refund-${p.id}`}>
                            Refund{inv?.number ? ` · ${inv.number}` : " · On Account"}
                          </span>
                        ) : p.kind === "advance" && !p.invoice_id ? (
                          <span className="inline-flex items-center text-[10px] uppercase tracking-wider bg-indigo-50 border border-indigo-200 text-indigo-700 rounded px-1.5 py-0.5 font-sans font-medium" data-testid={`pay-advance-${p.id}`}>
                            Advance · {p.sales_order_number || "On Account"}
                          </span>
                        ) : (
                          <>
                            {inv?.number || (p.invoice_id || "").slice(0, 8)}
                            {p.kind === "advance" && <span className="ml-1.5 text-[9px] uppercase text-indigo-500 font-sans" title={`Advance adjusted from ${p.sales_order_number || "on-account credit"}`}>adv</span>}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-600">{inv?.customer_snapshot?.company_name || p.customer_name || "-"}</td>
                      <td className="px-4 py-3">{p.mode}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-600">{p.reference}</td>
                      <td className={`px-4 py-3 text-right tabular-nums font-medium ${p.kind === "refund" ? "text-rose-600" : "text-emerald-700"}`}>{p.kind === "refund" ? `− ${inr(p.amount)}` : inr(p.amount)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {p.kind !== "refund" && (
                        <button onClick={() => sendReceipt(p)} className="p-1.5 text-slate-400 hover:text-emerald-600 transition-colors duration-200" title="Email receipt to customer (from accounts@hitechsilico.com)" data-testid={`pay-receipt-${p.id}`}>
                          <Mail size={14} />
                        </button>
                        )}
                        {p.kind !== "refund" && (
                        <button onClick={() => startEdit(p)} className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors duration-200" title="Edit payment" data-testid={`pay-edit-${p.id}`}>
                          <Pencil size={14} />
                        </button>
                        )}
                        <button onClick={() => del(p)} className="p-1.5 text-slate-400 hover:text-rose-600 transition-colors duration-200" title="Delete payment" data-testid={`pay-delete-${p.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {list.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-sm text-slate-500">No payments recorded.</td></tr>}
              </tbody>
            </table>
            <Pager pager={payPager} />
          </div>
        </TabsContent>

        <TabsContent value="ledger">
          <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]" data-testid="ledger-summary">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2.5">Customer</th>
                  <th className="text-right px-4 py-2.5 hidden md:table-cell">Invoices</th>
                  <th className="text-right px-4 py-2.5">Invoiced</th>
                  <th className="text-right px-4 py-2.5">Paid</th>
                  <th className="text-right px-4 py-2.5 hidden md:table-cell">Advance</th>
                  <th className="text-right px-4 py-2.5">Balance</th>
                  <th className="text-right px-4 py-2.5">Ledger</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((c) => (
                  <tr key={c.customer_id} className="border-t border-slate-100 hover:bg-slate-50/70">
                    <td className="px-4 py-3 font-medium text-slate-800">
                      {c.customer}
                      {c.overdue > 0 && <span className="ml-2 text-[10px] uppercase tracking-wider bg-rose-100 text-rose-700 rounded px-1.5 py-0.5">{c.overdue} overdue</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell">{c.invoices}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{inr(c.invoiced)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{inr(c.paid)}</td>
                    <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell text-indigo-600" data-testid={`ledger-advance-${c.customer_id}`}>{c.advance > 0 ? inr(c.advance) : "—"}</td>
                    <td className={`px-4 py-3 text-right tabular-nums font-semibold ${c.balance > 0 ? "text-rose-600" : "text-slate-700"}`}>{inr(c.balance)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openLedger(c)} data-testid={`ledger-open-${c.customer_id}`}>
                        <BookOpen size={12} className="mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
                {summary.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-sm text-slate-500">No invoices yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!ledger} onOpenChange={(v) => !v && setLedger(null)}>
        <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
          <DialogHeader><DialogTitle>Customer Ledger — {ledgerName}</DialogTitle></DialogHeader>
          {ledger && (
            <>
              <div className={`grid gap-3 text-sm ${ledger.advance_credit > 0 ? "grid-cols-2 md:grid-cols-4" : "grid-cols-3"}`}>
                <div className="border border-slate-200 rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Invoiced</p>
                  <p className="text-lg font-bold tabular-nums" data-testid="ledger-invoiced">{inr(ledger.total_invoiced)}</p>
                </div>
                <div className="border border-slate-200 rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Paid</p>
                  <p className="text-lg font-bold tabular-nums text-emerald-700" data-testid="ledger-paid">{inr(ledger.total_paid)}</p>
                </div>
                {ledger.advance_credit > 0 && (
                <div className="border border-indigo-200 bg-indigo-50/40 rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-wider text-indigo-500">Advance Credit</p>
                  <p className="text-lg font-bold tabular-nums text-indigo-700" data-testid="ledger-advance-credit">{inr(ledger.advance_credit)}</p>
                </div>
                )}
                <div className="border border-slate-200 rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Outstanding Balance</p>
                  <p className={`text-lg font-bold tabular-nums ${ledger.balance > 0 ? "text-rose-600" : "text-slate-800"}`} data-testid="ledger-balance">{inr(ledger.balance)}</p>
                </div>
              </div>
              <div className="border border-slate-200 rounded-md overflow-hidden mt-2">
                <table className="w-full text-sm" data-testid="ledger-entries">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-left px-3 py-2">Reference</th>
                      <th className="text-right px-3 py-2">Debit</th>
                      <th className="text-right px-3 py-2">Credit</th>
                      <th className="text-right px-3 py-2">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.entries.map((e, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-2">{fmtDate(e.date)}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${e.type === "Invoice" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>{e.type}</span>
                        </td>
                        <td className="px-3 py-2 text-slate-600 text-xs">{e.ref}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{e.debit ? inr(e.debit) : "-"}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{e.credit ? inr(e.credit) : "-"}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{inr(e.balance)}</td>
                      </tr>
                    ))}
                    {ledger.entries.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-sm text-slate-500">No transactions yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      </>)}
    </div>
  );
}
